import React, { useEffect, useRef } from 'react';
import { ITableColumnFieldsFragment } from '../../../../utils/apollo/graphqlSchema';
import { ErrorContainer, GridContainer } from './DataModelerCdwTableGridUI';
import useCdwTableColumnsData from './useCdwTableColumnsData';
import {
  useCdwTableGridCellRenderer,
  useCdwTableGridColumnWidths,
} from './renderUtils';
import { MultiGrid } from 'react-virtualized';
import { MultiGridTable } from '../../../../components/MultiGridTable';
import { IEditDerivedColumnModel } from './types';
import useFactTableAutoMapFields from './useFactTableAutoMapFields';
import WarningModal from '../../../../components/ConfirmationModal/WarningModal';

interface IProps {
  searchQuery: string;
  isEditMode: boolean;
  viewKey: string;
  tableKey: string;
  serverError: string | undefined;
  availableColumnsFromServer: ITableColumnFieldsFragment[];
  editDerivedColumnModel: IEditDerivedColumnModel;
}

const ROW_HEIGHT = 32;
const MIN_CONTAINER_HEIGHT = 300;
const MAX_ROWS = 25;

const DataModelerCdwTableGrid = ({
  searchQuery,
  isEditMode,
  viewKey,
  tableKey,
  serverError,
  availableColumnsFromServer,
  editDerivedColumnModel,
}: IProps) => {
  const multiGridRef = useRef<MultiGrid>(null);

  const columnsData = useCdwTableColumnsData({
    availableColumnsFromServer,
    viewKey,
    tableKey,
    isEditMode,
  });
  const columns = columnsData.columns;
  const { needAutoMap, autoMapColumns } = useFactTableAutoMapFields({
    tableKey,
    columns,
  });

  const {
    cellRenderer,
    rowCount,
    onConfirmUnmapColumn,
    onCancelUnmapColumn,
    confirmUnmapModalColumn,
  } = useCdwTableGridCellRenderer({
    searchQuery,
    isEditMode,
    viewKey,
    tableKey,
    columnsData,
    editDerivedColumnModel,
  });

  const containerHeight = Math.max(
    MIN_CONTAINER_HEIGHT,
    Math.min(MAX_ROWS, rowCount) * ROW_HEIGHT,
  );

  const { getColumnWidth, onResize } = useCdwTableGridColumnWidths({
    current: multiGridRef.current,
    hasScrollbar: rowCount > MAX_ROWS,
  });

  // XXX when we add a new fact table, we attempt to automatically discover existing
  // fields which this table is extending. This has to happen only once, when the new
  // fact table is loaded for the first time
  useEffect(() => {
    if (needAutoMap && autoMapColumns) {
      autoMapColumns();
    }
  }, [needAutoMap, autoMapColumns]);

  // XXX using an inlinie style height because it can vary too much and using styled-components will
  // generate a new component for each unique size (at least 20 distinct sizes)
  return (
    <GridContainer style={{ height: containerHeight }}>
      {serverError && <ErrorContainer>{serverError}</ErrorContainer>}
      <MultiGridTable
        ref={multiGridRef}
        columnCount={7}
        rowCount={rowCount}
        cellRenderer={cellRenderer}
        rowHeight={ROW_HEIGHT}
        columnWidthRenderer={getColumnWidth}
        onResize={onResize}
        fixedRowCount={1}
      />
      {confirmUnmapModalColumn && (
        <WarningModal
          onConfirm={onConfirmUnmapColumn}
          onCancel={onCancelUnmapColumn}
          trackContext="data modeler"
          trackConfirmAction="unmap column - confirm"
          trackCancelAction="unmap column - cancel"
        >
          This column has Derived definition, it will be lost if you unmap the
          column. Are you sure you want to continue?
        </WarningModal>
      )}
    </GridContainer>
  );
};

export default DataModelerCdwTableGrid;
